import { Color, Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useMemo, VFC } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled, { css } from 'styled-components';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { ThemeAttributes } from 'dashboard/models/theme';
import { ThemeSet } from 'dashboard/models/theme-set';
import { RootState } from 'dashboard/redux/store';
import { partitionInUseInquiryTemplates } from 'dashboard/views/DashboardThemeSetsEditorView/util';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import hoverableMixin from 'design-system/mixins/hoverable';
import { FontWeight } from 'design-system/variables/text';
import useConfirmation from 'lib/hooks/useConfirmation';
import useToggle from 'lib/hooks/useToggle';
import { GoogleFonts } from 'lib/interfaces/fonts';

const StyledCard = styled(Card)<{ vertical: boolean; clickable: boolean }>`
  ${({ clickable }) => (clickable ? hoverableMixin : '')};

  height: 250px;
  margin: 0;
  display: flex;
  flex-direction: ${({ vertical }) => (vertical ? 'column' : 'row')};
  border: 2px solid ${Color.BorderHairline};
`;

const PreviewSection = styled.div<{ vertical: boolean }>`
  display: flex;
  flex-direction: row;
  gap: ${Spacing[2]};
  justify-content: space-evenly;
  ${({ vertical }) =>
    vertical
      ? css`
          border-bottom: 1px solid ${Color.BorderHairline};
        `
      : css`
          border-right: 1px solid ${Color.BorderHairline};
        `}
  flex: ${({ vertical }) => (vertical ? '2' : '1')};
`;

const TextandButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacing[2]};
  padding: ${Spacing[3]} ${Spacing[5]};
  flex: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-drection: column-reverse;
  justify-content: flex-end;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 70%;
`;

const ThemeTitle = styled.div`
  font-weight: ${FontWeight.Medium};
  font-size: ${({ theme }) => theme.text.paragraph.lgBold.fontSize};
  line-height: 150%;
  color: ${({ theme }) => theme.color.text.primary};
  overflow: hidden;
  white-space: nowrap;

  text-overflow: ellipsis;
`;

const AdditionalInfoText = styled(Paragraph)`
  font-size: ${({ theme }) => theme.text.paragraph.xs.fontSize};
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const VisualSamplePreviewsContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  flex-grow: 1;
`;

const RoundDotsContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

const BackgroundCardLarge = styled('div')`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: ${Color.BackgroundSecondary};
  flex-grow: 2;
  margin: ${Spacing[2]} ${Spacing[2]} ${Spacing[2]} 0;
  border-radius: ${Spacing[2]};
`;

const BackgroundCardSmallTop = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  background-color: ${Color.BackgroundSecondary};
  width: 95%;
  height: 50%;
  flex-grow: 1;
  align-items: center;
  margin: ${Spacing[2]} 0 0 ${Spacing[2]};
  border-radius: ${Spacing[2]};
`;

const BackgroundCardSmallBottom = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  background-color: ${Color.BackgroundSecondary};
  align-items: center;
  width: 95%;
  height: 50%;
  flex-grow: 1;
  margin: 0 0 ${Spacing[2]} ${Spacing[2]};
  border-radius: ${Spacing[2]};
`;

const FontDisplay = styled.div<{ font: string }>`
  font-family: ${({ font }) => font};
  font-size: ${({ theme }) => theme.text.heading.md};
`;

const RoundColorDot = styled('div')<{ color: string }>`
  background-color: ${({ color }) => color};
  border-radius: 50%;
  height: 40px;
  width: 40px;
`;
// These styles are copied over from our relevant implementation of the primary button:
// at: 'front-end/inquiry/components/Button/Button.tsx'
const StyledButton = styled('button')<{ themeAttributes: ThemeAttributes }>`
  background-color: ${({ themeAttributes }) =>
    themeAttributes['color-button-primary'] ??
    themeAttributes['color-primary'] ??
    Color.PrimaryPurple};
  border: ${({ themeAttributes }) =>
    `${themeAttributes['border-width']} solid ${
      themeAttributes['color-button-primary'] ??
      themeAttributes['color-primary'] ??
      Color.PrimaryPurple
    }`};
  color: ${({ themeAttributes }) =>
    themeAttributes['color-font-button-primary'] ?? Color.PrimaryWhite};

  ${({ themeAttributes }) =>
    themeAttributes['button-shadow-strength'] &&
    themeAttributes['button-shadow-strength'] > 0 &&
    css`
      box-shadow: 0 3px 10px 0px ${Color.CoreSlate500};
    `};

  border-radius: ${({ themeAttributes }) => themeAttributes['border-radius'] ?? Spacing[1]};
  height: 48px;
  text-transform: ${({ themeAttributes }) => themeAttributes['button-text-transform']};
  padding: 0 ${Spacing[2]};
  font-size: ${Spacing[4]};

  display: flex;
  align-items: center;
  justify-content: center;
  width: 90%;
  font-weight: ${({ themeAttributes }) =>
    themeAttributes['button-font-weight'] ?? FontWeight.Medium};
  text-decoration: none;
  white-space: nowrap;
`;

const TitleAndStickerContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const OrgDefaultSticker = styled(Sticker)`
  margin-left: ${Spacing[2]};
`;

const StyledText = styled('span')`
  font-size: ${({ theme }) => theme.text.paragraph.xs.fontSize};
  color: ${({ theme }) => theme.color.text.brand};
  cursor: pointer;
`;

export interface ThemeSetCardProps {
  themeSet: ThemeSet;
  inquiryTemplates?: InquiryTemplate[];
  onCloneThemeSet?: (themeSet: ThemeSet) => Promise<void>;
  onArchiveThemeSet?: (themeSet: ThemeSet) => void;
  onClickThemeSetCard?: (themeSet: ThemeSet) => void;
  onUpdateOrgDefaultThemeSet?: (themeSet: ThemeSet) => void;
  vertical?: boolean;
  linkToEditor?: boolean;
}

const ThemeSetCard: VFC<ThemeSetCardProps> = ({
  themeSet,
  inquiryTemplates = [],
  onCloneThemeSet,
  onArchiveThemeSet,
  onClickThemeSetCard,
  onUpdateOrgDefaultThemeSet,
  vertical = true,
  linkToEditor = false,
}) => {
  const navigate = useNavigate();

  const [dropdownOpened, { enable: openDropdown, disable: hideDropdown }] = useToggle(false);
  const [optionsDropdownOpened, { enable: openOptionsDropdown, disable: hideOptionsDropdown }] =
    useToggle(false);

  const [
    showUpdateDefaultThemeSetModal,
    { prompt: promptUpdateDefault, confirm: confirmUpdateDefault, cancel: cancelUpdateDefault },
  ] = useConfirmation();
  const [
    showArchiveThemeSetModal,
    {
      prompt: promptArchiveThemeSet,
      confirm: confirmArchiveThemeSet,
      cancel: cancelArchiveThemeSet,
    },
  ] = useConfirmation();

  const orgDefaultThemeSetId = useSelector<RootState, string | null>(
    (state) => state.organization.organization?.relationships['theme-set'].data?.id,
  );

  const associatedInquiryTemplates = useMemo(() => {
    // filter templates that use the currently selected theme as default.
    const [inUseInquiryTemplates] = partitionInUseInquiryTemplates(themeSet, inquiryTemplates);

    // construct count string (singular for count of 1, plural otherwise)
    const templateCount = inUseInquiryTemplates.length;
    const templateNounForm = templateCount === 1 ? 'template' : 'templates';
    const countLabel = ` ${templateCount} inquiry ${templateNounForm}`;

    const templateLinks = inUseInquiryTemplates.map((template, idx) => {
      const partialUrl = `${template.id}/${template.relationships['latest-draft-version'].data?.id ?? template.relationships['latest-published-version'].data?.id}/flow`;

      return (
        <DropdownMenu.Item
          id={`themeSet-card__template-dropdown-${idx}`}
          key={`themeSet-card__template-dropdown-${idx}`}
          variant='basic'
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate(`/dashboard/inquiry-templates/${partialUrl}`);
          }}
        >
          <StyledText>{template.attributes['name-display']}</StyledText>
        </DropdownMenu.Item>
      );
    });

    return { countLabel, templateLinks };
  }, [themeSet, inquiryTemplates]);

  const menuItems: JSX.Element[] = [];
  if (onCloneThemeSet) {
    menuItems.push(
      <DropdownMenu.Item
        id='themeSet-card__dropdown-clone'
        key='clone'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCloneThemeSet(themeSet);
        }}
      >
        Duplicate
      </DropdownMenu.Item>,
    );
  }
  if (onUpdateOrgDefaultThemeSet && themeSet.id !== orgDefaultThemeSetId) {
    menuItems.push(
      <DropdownMenu.Item
        id='themeSet-card__dropdown-make-organization-default'
        key='make-organization-default'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          promptUpdateDefault(() => onUpdateOrgDefaultThemeSet(themeSet));
        }}
      >
        Set theme as organization default
      </DropdownMenu.Item>,
    );
  }
  if (onArchiveThemeSet) {
    menuItems.push(
      <DropdownMenu.Item
        id='themeSet-card__dropdown-deactivate'
        key='deactivate'
        variant='danger'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          promptArchiveThemeSet(() => onArchiveThemeSet(themeSet));
        }}
      >
        Archive
      </DropdownMenu.Item>,
    );
  }

  const onMouseLeaveCard = () => {
    hideDropdown();
    hideOptionsDropdown();
  };

  const lightTheme = themeSet.attributes['light-theme'];
  // we use `Rubik` as our default font per: 'front-end/dashboard/lib/theme.ts`
  const font = lightTheme['font-family-title'] ?? GoogleFonts.Rubik;

  const styledCard = (
    <StyledCard
      vertical={vertical}
      clickable={onClickThemeSetCard}
      onClick={() => onClickThemeSetCard?.(themeSet)}
      id={`theme-set-card__${themeSet.id}`}
      onMouseLeave={onMouseLeaveCard}
    >
      <PreviewSection vertical={vertical}>
        <VisualSamplePreviewsContainer>
          <BackgroundCardSmallTop>
            <FontDisplay font={font}>{font}</FontDisplay>
          </BackgroundCardSmallTop>
          <BackgroundCardSmallBottom>
            <StyledButton
              themeAttributes={themeSet.attributes['light-theme']}
              id={`theme-set-card-button-preview__${themeSet.id}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Primary Button
            </StyledButton>
          </BackgroundCardSmallBottom>
        </VisualSamplePreviewsContainer>
        <BackgroundCardLarge>
          <RoundDotsContainer>
            <RoundColorDot color={lightTheme['color-primary'] ?? Color.PrimaryPurple} />
            <RoundColorDot color={lightTheme['color-font-title'] ?? Color.PrimaryBlack} />
            <RoundColorDot color={lightTheme['color-modal-background'] ?? Color.PrimaryWhite} />
          </RoundDotsContainer>
        </BackgroundCardLarge>
      </PreviewSection>
      <TextandButtonSection>
        <InfoContainer>
          <TitleAndStickerContainer>
            <ThemeTitle>{themeSet.attributes.name}</ThemeTitle>
            {themeSet.id === orgDefaultThemeSetId && (
              <OrgDefaultSticker variant='basic'>Org default</OrgDefaultSticker>
            )}
          </TitleAndStickerContainer>
          <HoverCopyableField copyValue={themeSet.id} confirmationText='Copied Theme ID'>
            <AdditionalInfoText>{themeSet.id}</AdditionalInfoText>
          </HoverCopyableField>
          <AdditionalInfoText>{`Last edited at ${DateTime.fromISO(
            themeSet.attributes['updated-at'],
          ).toRelative()}`}</AdditionalInfoText>
          {vertical && themeSet.id !== orgDefaultThemeSetId ? (
            <AdditionalInfoText>
              This theme is used by
              {associatedInquiryTemplates.templateLinks.length === 0 ? (
                <span>{associatedInquiryTemplates.countLabel}</span>
              ) : (
                <DropdownMenu
                  contentProps={{
                    onEscapeKeyDown: hideDropdown,
                    onInteractOutside: hideDropdown,
                  }}
                  open={dropdownOpened}
                  onOpenChange={(opened) => opened && openDropdown()}
                  side='right'
                  trigger={
                    <StyledText
                      onMouseEnter={openDropdown}
                      id={`theme-set-card_template-menu-${themeSet.id}`}
                    >
                      {associatedInquiryTemplates.countLabel}
                    </StyledText>
                  }
                >
                  {associatedInquiryTemplates.templateLinks}
                </DropdownMenu>
              )}
            </AdditionalInfoText>
          ) : null}
        </InfoContainer>
        <ButtonContainer>
          {menuItems.length > 0 && (
            <DropdownMenu
              contentProps={{
                onEscapeKeyDown: hideOptionsDropdown,
                onInteractOutside: hideOptionsDropdown,
              }}
              open={optionsDropdownOpened}
              onOpenChange={(opened) => opened && openOptionsDropdown()}
              trigger={
                <Button
                  aria-label={`theme-set-card_actions-menu-${themeSet.id}`}
                  variant='basic'
                  width='icon'
                  id={`theme-set-card_actions-menu-${themeSet.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <MoreIcon />
                </Button>
              }
            >
              {menuItems}
            </DropdownMenu>
          )}
        </ButtonContainer>
      </TextandButtonSection>
    </StyledCard>
  );

  return (
    <>
      <ModalWrapper isOpen={showUpdateDefaultThemeSetModal} handleCloseModal={cancelUpdateDefault}>
        <ConfirmationModal
          cancelAction={cancelUpdateDefault}
          confirmAction={confirmUpdateDefault}
          header='Update organization default theme?'
          confirmActionText='Update'
        >
          <p>This theme will become the default theme of this organization</p>
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={showArchiveThemeSetModal} handleCloseModal={cancelArchiveThemeSet}>
        <ConfirmationModal
          cancelAction={cancelArchiveThemeSet}
          confirmAction={confirmArchiveThemeSet}
          header='Archive this theme?'
          confirmActionText='Archive'
        >
          <p>
            This action is permanent and can’t be undone. Please ensure that this theme is not in
            use before continuing.
          </p>
        </ConfirmationModal>
      </ModalWrapper>
      {linkToEditor ? (
        <Link to={`/dashboard/theme-sets/${themeSet.id}`} key={themeSet.id}>
          {styledCard}
        </Link>
      ) : (
        styledCard
      )}
    </>
  );
};
export default ThemeSetCard;
